import BoldIcon from '@mui/icons-material/FormatBold'
import ItalicIcon from '@mui/icons-material/FormatItalic'
import ListIcon from '@mui/icons-material/FormatListBulleted'
import NumberedListIcon from '@mui/icons-material/FormatListNumbered'
import UnderlineIcon from '@mui/icons-material/FormatUnderlined'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import React, { useEffect, useRef } from 'react'

const ToolbarButton = ({ active, disabled, onClick, label, children }) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        disabled={disabled}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border text-k-ink disabled:cursor-not-allowed disabled:opacity-40 ${active
            ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
            : 'border-transparent hover:bg-k-hover'}`}
        onMouseDown={(event) => {
            event.preventDefault()
            onClick()
        }}
    >
        {children}
    </button>
)

const normalizeHtml = (html) => String(html || '')
    .replace(/\s+/g, ' ')
    .replace(/>\s+</g, '><')
    .trim()

/**
 * @param {{
 *   value?: string,
 *   onChange?: (html: string) => void,
 *   disabled?: boolean,
 *   label?: import('react').ReactNode,
 *   required?: boolean,
 *   error?: boolean,
 *   helperText?: import('react').ReactNode,
 *   minHeightClass?: string,
 *   extensions?: import('@tiptap/react').AnyExtension[],
 *   onReady?: (editor: import('@tiptap/react').Editor | null) => void,
 * }} props
 */
const HtmlEditor = ({
    value = '',
    onChange,
    disabled = false,
    label = 'Mensaje',
    required = false,
    error = false,
    helperText,
    minHeightClass = 'min-h-[16rem]',
    extensions = [],
    onReady,
}) => {
    const applyingRef = useRef(false)
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                codeBlock: false,
                blockquote: false,
                horizontalRule: false,
            }),
            Underline,
            ...extensions,
        ],
        content: value || '',
        editable: !disabled,
        immediatelyRender: false,
        editorProps: {
            attributes: {
                class: [
                    'html-editor-content outline-none',
                    minHeightClass,
                    'px-1 py-1 text-[15px] font-medium leading-relaxed text-k-ink',
                    '[&_p]:m-0 [&_p+p]:mt-2',
                    '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
                    '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
                    '[&_a]:text-k-primary',
                ].join(' '),
            },
        },
        onUpdate: ({ editor: current }) => {
            if (applyingRef.current || current.isDestroyed) return
            onChangeRef.current?.(current.getHTML())
        },
    })

    useEffect(() => {
        if (!editor) return undefined
        editor.setEditable(!disabled)
        return undefined
    }, [editor, disabled])

    useEffect(() => {
        if (!editor || !onReady) return undefined
        onReady(editor)
        return () => onReady(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor])

    useEffect(() => {
        if (!editor) return undefined
        const next = value || ''
        if (normalizeHtml(next) === normalizeHtml(editor.getHTML())) return undefined
        applyingRef.current = true
        editor.commands.setContent(next, { emitUpdate: false })
        queueMicrotask(() => {
            applyingRef.current = false
        })
        return undefined
    }, [editor, value])

    useEffect(() => () => {
        applyingRef.current = true
    }, [])

    return (
        <div className={`flex flex-col gap-1.5 ${disabled ? 'opacity-70' : ''}`}>
            <span className={`text-[13px] font-semibold ${error ? 'text-k-danger' : 'text-k-ink'}`}>
                {label}
                {required && <span className="text-k-danger"> *</span>}
            </span>
            <div className={`overflow-hidden rounded-xl border bg-k-field ${error ? 'border-2 border-k-danger' : 'border-k-line-strong'} ${disabled ? 'bg-k-sunken' : ''}`}>
                <div className="flex flex-wrap gap-0.5 border-b border-k-line px-2 py-1.5">
                    <ToolbarButton
                        label="Negrita"
                        disabled={disabled || !editor}
                        active={editor?.isActive('bold')}
                        onClick={() => editor?.chain().focus().toggleBold().run()}
                    >
                        <BoldIcon style={{ fontSize: 18 }} />
                    </ToolbarButton>
                    <ToolbarButton
                        label="Cursiva"
                        disabled={disabled || !editor}
                        active={editor?.isActive('italic')}
                        onClick={() => editor?.chain().focus().toggleItalic().run()}
                    >
                        <ItalicIcon style={{ fontSize: 18 }} />
                    </ToolbarButton>
                    <ToolbarButton
                        label="Subrayado"
                        disabled={disabled || !editor}
                        active={editor?.isActive('underline')}
                        onClick={() => editor?.chain().focus().toggleUnderline().run()}
                    >
                        <UnderlineIcon style={{ fontSize: 18 }} />
                    </ToolbarButton>
                    <ToolbarButton
                        label="Lista"
                        disabled={disabled || !editor}
                        active={editor?.isActive('bulletList')}
                        onClick={() => editor?.chain().focus().toggleBulletList().run()}
                    >
                        <ListIcon style={{ fontSize: 18 }} />
                    </ToolbarButton>
                    <ToolbarButton
                        label="Lista numerada"
                        disabled={disabled || !editor}
                        active={editor?.isActive('orderedList')}
                        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                    >
                        <NumberedListIcon style={{ fontSize: 18 }} />
                    </ToolbarButton>
                </div>
                <div className="px-3 py-2">
                    <EditorContent editor={editor} />
                </div>
            </div>
            {helperText && (
                <span className={`text-xs font-medium ${error ? 'text-k-danger' : 'text-k-muted'}`}>{helperText}</span>
            )}
        </div>
    )
}

export default HtmlEditor
